import type { MaybeComputedElementRef, MaybeElement } from '@vueuse/core';
import type { Ref } from 'vue';
import type { Panel } from '#layers/workspace/types/panel';
import { useElementSize, useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, markRaw, reactive, ref, watch } from 'vue';
import { toSortedArray, workspaceScopedKey } from '#layers/base/public';

export const SIDEBAR_MIN_SIZE = 220;
export const SIDEBAR_DEFAULT_SIZE = 290;
export const SIDEBAR_MAX_SIZE = 360;

export const PANEL_MIN_SIZE = 220;
export const PANEL_DEFAULT_SIZE = 360;
export const PANEL_MAX_SIZE = 500;

export const CONTENT_MIN_SIZE = 550;

type PaneId = 'panel' | 'sidebar';

export const usePaneStore = defineStore('workspace-pane-store', () => {
	const registeredPanels = reactive(new Map<string, Panel>());
	const currentPanelId = useLocalStorage<string | null>(workspaceScopedKey('workspace-panel-current'), null);

	const registerPanel = (panel: Panel) => {
		const registeredPanel: Panel = {
			...panel,
			component: markRaw(panel.component),
		};

		if (panel.toggleComponent) {
			registeredPanel.toggleComponent = markRaw(panel.toggleComponent);
		}

		registeredPanels.set(panel.id, registeredPanel);
	};

	const unregisterPanel = (panel: Panel) => {
		registeredPanels.delete(panel.id);
	};

	const panels = computed(() => {
		return toSortedArray([...registeredPanels.values()], 'order');
	});

	const currentPanel = computed<Panel | null>(() => {
		const selectedPanel = currentPanelId.value ? registeredPanels.get(currentPanelId.value) : undefined;

		return selectedPanel ?? panels.value[0] ?? null;
	});

	const availablePaneWidth = ref<number>(-1);

	const syncAvailablePaneWidth = (el: MaybeComputedElementRef<MaybeElement>) => {
		const { width: elementWidth } = useElementSize(el);

		watch(elementWidth, (width) => {
			if (width <= 0 && availablePaneWidth.value > 0) return;

			availablePaneWidth.value = width;
		}, { immediate: true });
	};

	const panelRoom = computed(() => {
		if (availablePaneWidth.value > CONTENT_MIN_SIZE + SIDEBAR_MAX_SIZE + PANEL_MAX_SIZE) return 2;
		if (availablePaneWidth.value > CONTENT_MIN_SIZE + Math.max(SIDEBAR_MAX_SIZE, PANEL_MAX_SIZE)) return 1;
		return 0;
	});

	const sidebarVisibleState = useLocalStorage(workspaceScopedKey('workspace-sidebar-visible'), true);
	const sidebarSize = useLocalStorage(workspaceScopedKey('workspace-sidebar-size'), SIDEBAR_DEFAULT_SIZE);

	const panelVisibleState = useLocalStorage(workspaceScopedKey('workspace-panel-visible'), false);
	const panelSize = useLocalStorage(workspaceScopedKey('workspace-panel-size'), PANEL_DEFAULT_SIZE);

	const lastOpenedPane = ref<PaneId | null>(null);

	watch(sidebarSize, (newSize) => {
		sidebarSize.value = Math.max(SIDEBAR_MIN_SIZE, Math.min(newSize, SIDEBAR_MAX_SIZE));
	}, { immediate: true });

	watch(panelSize, (newSize) => {
		panelSize.value = Math.max(PANEL_MIN_SIZE, Math.min(newSize, PANEL_MAX_SIZE));
	}, { immediate: true });

	const getPaneVisibleState = (pane: PaneId) => {
		return pane === 'panel' ? panelVisibleState : sidebarVisibleState;
	};

	const getOtherPane = (pane: PaneId): PaneId => {
		return pane === 'panel' ? 'sidebar' : 'panel';
	};

	const setPaneVisible = (pane: PaneId, visible: boolean) => {
		const paneVisible = getPaneVisibleState(pane);

		if (paneVisible.value === visible) return;

		paneVisible.value = visible;

		if (visible) {
			lastOpenedPane.value = pane;

			if (panelRoom.value === 1) {
				getPaneVisibleState(getOtherPane(pane)).value = false;
			}
		}
		else if (lastOpenedPane.value === pane) {
			lastOpenedPane.value = null;
		}
	};

	const togglePane = (pane: PaneId, open?: boolean) => {
		setPaneVisible(pane, open ?? !getPaneVisibleState(pane).value);
	};

	const sidebarVisible = useVisible('sidebar');
	const panelVisible = useVisible('panel');

	const sidebarUseSplitPanel = computed(() => panelRoom.value > 0);

	const panelUseSplitPanel = computed(() => {
		return currentPanel.value !== null
			&& panelRoom.value > 0;
	});

	const sidebarDrawerVisible = useDrawerVisible(sidebarUseSplitPanel);
	const panelDrawerVisible = useDrawerVisible(panelUseSplitPanel);

	const sidebarOpen = computed(() => (sidebarUseSplitPanel.value ? sidebarVisible.value : sidebarDrawerVisible.value));
	const panelOpen = computed(() => (panelUseSplitPanel.value ? panelVisible.value : panelDrawerVisible.value));

	watch(panelRoom, (room) => {
		if (room !== 1) return;
		if (!panelVisibleState.value || !sidebarVisibleState.value) return;

		setPaneVisible(lastOpenedPane.value === 'sidebar' ? 'panel' : 'sidebar', false);
	});

	const toggleSidebar = (open?: boolean) => {
		if (sidebarUseSplitPanel.value) {
			togglePane('sidebar', open);
		}
		else {
			sidebarDrawerVisible.value = open ?? !sidebarDrawerVisible.value;
		}
	};

	const togglePanel = (opts?: { panelId?: string; open?: boolean }) => {
		const panelIdChanged = !!opts?.panelId && opts.panelId !== currentPanel.value?.id;

		if (opts?.panelId) {
			currentPanelId.value = opts.panelId;
		}

		if (panelUseSplitPanel.value) {
			if (panelIdChanged) {
				setPaneVisible('panel', opts?.open ?? true);
			}
			else {
				togglePane('panel', opts?.open);
			}
		}
		else if (panelIdChanged) {
			panelDrawerVisible.value = opts?.open ?? true;
		}
		else {
			panelDrawerVisible.value = opts?.open ?? !panelDrawerVisible.value;
		}
	};

	const sidebarSplitPanelCollapsed = useSplitPanelCollapsed('sidebar', sidebarUseSplitPanel);
	const panelSplitPanelCollapsed = useSplitPanelCollapsed('panel', panelUseSplitPanel);

	function useVisible(pane: PaneId) {
		return computed({
			get: () => getPaneVisibleState(pane).value,
			set: (visible: boolean) => setPaneVisible(pane, visible),
		});
	}

	function useDrawerVisible(paneUseSplitPanel: Ref<boolean>) {
		const internalVisible = ref(false);

		watch(paneUseSplitPanel, (isSplit) => {
			if (isSplit) internalVisible.value = false;
		});

		return computed({
			get: () => (paneUseSplitPanel.value ? false : internalVisible.value),
			set: (visible: boolean) => {
				if (!paneUseSplitPanel.value) internalVisible.value = visible;
			},
		});
	}

	function useSplitPanelCollapsed(pane: PaneId, paneUseSplitPanel: Ref<boolean>) {
		return computed({
			get: () => !paneUseSplitPanel.value || !getPaneVisibleState(pane).value,
			set: (collapsed: boolean) => {
				if (paneUseSplitPanel.value) setPaneVisible(pane, !collapsed);
			},
		});
	}

	return {
		availablePaneWidth,
		syncAvailablePaneWidth,
		sidebarVisible,
		sidebarOpen,
		sidebarDrawerVisible,
		sidebarSize,
		toggleSidebar,
		sidebarUseSplitPanel,
		sidebarSplitPanelCollapsed,
		panelVisible,
		panelOpen,
		panelDrawerVisible,
		panelSize,
		togglePanel,
		panelUseSplitPanel,
		panelSplitPanelCollapsed,
		currentPanelId,
		currentPanel,
		registerPanel,
		unregisterPanel,
		panels,
	};
});
